import { IconArrowLeft, IconX } from '@tabler/icons-react'
import { Modal as AntModal, ModalProps as AntModalProps } from 'antd'
import styled, { css } from 'styled-components'

import { Button } from './Button'

import { NarrowContent } from '@/components/layout-components/CorePageViewEntity'

type StyledModalProps = {
  noPadding?: boolean
  fullscreen?: boolean
}

export const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`

export const FooterSplitWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`

const StyledModal = styled(AntModal)<StyledModalProps>`
  & .ant-modal-content {
    position: relative;
    border-radius: ${({ theme }) => theme.borderRadius.large};
    overflow: hidden; // ensure rounded cornerns when settings a background colour

    & .ant-modal-header {
      border-bottom-color: ${({ theme }) => theme.colour.neutral.surface};
      padding-left: 1.25rem;
      padding-right: 1.25rem;

      & .ant-modal-title {
        ${({ theme }) => theme.font.label01}
        color: ${({ theme }) => theme.colour.secondary._900};
      }

      ${({ theme: { media } }) => media.sm`
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      `}
    }

    & .ant-modal-body {
      padding: ${({ noPadding }) => (noPadding ? '0' : '1.25rem')};

      ${({ noPadding, theme: { media } }) => media.sm`
        padding: ${noPadding ? '0' : '2.5rem'};
      `}
    }

    & .ant-modal-footer {
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      border-top-color: ${({ theme }) => theme.colour.secondary._300};
      padding: 0.75rem 0;
    }
  }

  ${({ fullscreen }) =>
    fullscreen &&
    css`
      &,
      & .ant-modal-content {
        width: 100dvw !important;
        height: 100dvh; // account for floating address bar
        margin: 0;
        top: 0;
        border-radius: 0;

        & .ant-modal-body {
          height: 100%;
          overflow-y: scroll;
        }

        & .ant-modal-footer {
          position: absolute;
          bottom: 0;
          width: 100%;
          margin: 0;
          padding: 1.5rem;
          z-index: 1036;
          background-color: ${({ theme }) => theme.colour.neutral.white};
        }
      }
    `}
`

const SplitFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`

const SpacedFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`

const CenterFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`

const getFooterLayout = (
  footer?: Props['footer'],
  footerLayout?: Props['footerLayout']
) => {
  if (!footer) return null

  if (footerLayout === 'split') return <SplitFooter>{footer}</SplitFooter>
  if (footerLayout === 'spaced') return <SpacedFooter>{footer}</SpacedFooter>
  if (footerLayout === 'center') return <CenterFooter>{footer}</CenterFooter>
  return <>{footer}</>
}

type Props = AntModalProps & {
  noPadding?: boolean
  fullscreen?: boolean
  footerLayout?: 'split' | 'spaced' | 'center'
}

export const Modal: React.FC<Props> = ({
  noPadding,
  fullscreen,
  footer = null,
  footerLayout,
  children,
  onCancel,
  ...rest
}) => {
  return (
    <StyledModal
      closeIcon={onCancel ? <IconX size={14} /> : null}
      onCancel={onCancel}
      closable={!!onCancel}
      footer={getFooterLayout(footer, footerLayout)}
      maskStyle={{ backgroundColor: `rgba(0,0,0,0.7)` }}
      noPadding={noPadding}
      fullscreen={fullscreen}
      {...rest}
    >
      {children}
    </StyledModal>
  )
}

export type ModalProps = Props

const StyledFullscreenModal = styled(AntModal)`
  &,
  & .ant-modal-content {
    width: 100vw !important;
    height: 100vh;
    margin: 0;
    top: 0;
    border-radius: 0;

    & .ant-modal-body {
      height: 100%;
      overflow-y: scroll;
      padding: 1.5rem;
    }

    & .ant-modal-footer {
      position: absolute;
      bottom: 0;
      width: 100%;
      margin: 0;
      padding: 1.5rem;
      z-index: 1036;
      background-color: ${({ theme }) => theme.colour.neutral.white};
    }
  }
`

const FullscreenModalHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const FullscreenModalTitles = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.5rem;
`

const FullscreenModalTitle = styled.span`
  ${({ theme }) => theme.font.heading02}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._900};
  display: block;
`

const FullscreenModalDescription = styled.span`
  color: ${({ theme }) => theme.colour.secondary._600};
  display: block;
`

export type FullscreenModalProps = AntModalProps & {
  title: React.ReactNode
  description?: React.ReactNode
  onBack?: () => void
  footerLayout?: 'split' | 'spaced' | 'center'
}

export const FullscreenModal: React.FC<FullscreenModalProps> = ({
  title,
  description,
  onCancel,
  onBack,
  footer = null,
  footerLayout,
  children,
  ...modalProps
}) => {
  return (
    <StyledFullscreenModal
      {...modalProps}
      closable={false}
      onCancel={onCancel}
      footer={getFooterLayout(footer, footerLayout)}
    >
      <FullscreenModalHeader>
        {onBack ? (
          <Button
            variant="secondary-text"
            shape="circle"
            icon={<IconArrowLeft size={24} />}
            onClick={onBack}
          />
        ) : (
          <span data-reason="flex-space-between" />
        )}

        <Button
          variant="secondary-text"
          shape="circle"
          icon={<IconX size={24} />}
          onClick={onCancel}
        />
      </FullscreenModalHeader>

      <NarrowContent column>
        <FullscreenModalTitles>
          <FullscreenModalTitle>{title}</FullscreenModalTitle>
          {description && (
            <FullscreenModalDescription>
              {description}
            </FullscreenModalDescription>
          )}
        </FullscreenModalTitles>

        {children}
      </NarrowContent>
    </StyledFullscreenModal>
  )
}

Modal.displayName = 'Modal'
FullscreenModal.displayName = 'FullscreenModal'
